import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
	Alert,
	Card,
	Col,
	Row,
	Select,
	Form,
	Input,
	AutoComplete,
	Typography,
	Tooltip,
	Button,
} from 'antd';
import { apiV2 } from 'services/api';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { LanguageContext } from 'contexts/LanguageContext';
import type { Auths } from 'interfaces/bitrix24';
import { Template } from 'interfaces/template';
import { FormattedMessage } from 'react-intl';
import { LocationSearch } from 'components/MessageCard/LocationSearch';
const { Text, Title } = Typography;

interface TemplateFetchError {
	message: string;
	code?: string | number;
	type?: string;
	fbtrace_id?: string;
}

interface CampaignTemplateMessageProps {
	isStatic: boolean;
	selectedTemplate: Template | null;
	params: string[];
	message?: string;
	templateList: Template[];
	templateError: string;
	templateFetchError: TemplateFetchError | null;
	paramsErrors?: string[];
	onChangeSelectedTemplate: (template: Template) => void;
	onChangeParams: (params: string[]) => void;
	onChangeLocation: (params: Location) => void;
}

interface TemplateButton {
	text: string;
	type: string;
	url?: string;
}

interface ButtonParts {
	original: TemplateButton;
	text: string[];
	variables: string[];
}

interface ComponentParts {
	type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
	text?: string;
	buttons?: TemplateButton[];
}

export interface Location {
	latitude: number;
	longitude: number;
	name?: string | null;
	address?: string | null;
}

const PLACEHOLDER_RX = /{{[^}]+}}/g;
const WHATSAPP_TAG_TO_HTML_MAP: Record<string, string> = {
	'*': '<strong>$1</strong>',
	_: '<em>$1</em>',
	'~': '<s>$1</s>',
	'```': '<code>$1</code>',
};

const convertWhatsappTagsToHTML = (text: string): string => {
	let html = text;
	for (const tag in WHATSAPP_TAG_TO_HTML_MAP) {
		if (tag === '*') html = html.replace(/\*([^*]+)\*/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
		else if (tag === '_') html = html.replace(/_(.*?)_/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
		else if (tag === '~') html = html.replace(/~(.*?)~/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
		else if (tag === '```') html = html.replace(/```([\s\S]*?)```/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
	}
	return html;
};

const splitTextWithPlaceholders = (text: string | undefined): string[] => {
	if (!text) return [];
	return text
		.replace(PLACEHOLDER_RX, '{{}}')
		.split(/({{}})/)
		.filter((part) => part !== '');
};

const countPlaceholdersInText = (text: string | undefined): number => {
	if (!text) return 0;
	const matches = text.match(PLACEHOLDER_RX);
	return matches ? matches.length : 0;
};

function CampaignTemplateMessage(props: CampaignTemplateMessageProps) {
	const { dictionary } = useContext(LanguageContext);
	const bitrix24 = useContext(Bitrix24Context);

	const [auths, setAuths] = useState<Auths>({ access_token: '', refresh_token: '' });
	const [domain, setDomain] = useState<string | undefined>();
	const [variables, setVariables] = useState<string[]>([]);
	const [filteredVariables, setFilteredVariables] = useState<string[]>([]);
	const [currentTemplate, setCurrentTemplate] = useState<Template | undefined>();
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean[]>([]);
	const [internalParams, setInternalParams] = useState<string[]>([]);

	const [headerType, setHeaderType] = useState<string | undefined>();
	const [headerMedia, setHeaderMedia] = useState<string | undefined>();
	const [location, setLocation] = useState<Location | null>();
	const [headerParts, setHeaderParts] = useState<string[]>([]);
	const [bodyParts, setBodyParts] = useState<string[]>([]);
	const [buttonParts, setButtonParts] = useState<ButtonParts[]>([]);
	const [footerText, setFooterText] = useState<string>('');

	const templateErrorStatus = props.templateError ? 'error' : undefined;

	useEffect(() => {
		if (!bitrix24?.infos?.domain) return;
		setDomain(bitrix24.infos.domain);
		setAuths(bitrix24.auth);
	}, [bitrix24?.auth, bitrix24?.infos?.domain]);

	useEffect(() => {
		setInternalParams(props.params || []);
	}, []);

	useEffect(() => {
		if (!domain) return;
		const fetchVariables = async () => {
			try {
				const res = await apiV2.get(`/variables/${domain}`, {
					headers: {
						'Access-Token': auths.access_token,
						'Refresh-Token': auths.refresh_token,
					},
				});
				setVariables(res.data || []);
			} catch (err) {
				console.error('Erro ao buscar variáveis:', err);
				setVariables([]);
			}
		};
		if (!props.isStatic) fetchVariables();
	}, [domain, auths.access_token, auths.refresh_token]);

	useEffect(() => {
		const initialTemplateIndex = props.templateList.findIndex(
			(t) => t.id === props.selectedTemplate?.id,
		);
		if (initialTemplateIndex >= 0) {
			handleTemplateChange(initialTemplateIndex);
		}
	}, [props.selectedTemplate, props.templateList]);

	const handleTemplateChange = useCallback(
		(index: number) => {
			const template = props.templateList[index];
			if (!template) return;

			setCurrentTemplate(template);

			const getComponent = (type: ComponentParts['type']) =>
				template.components?.find((c: ComponentParts) => c.type === type);

			const headerComp = getComponent('HEADER');
			const bodyComp = getComponent('BODY');
			const footerComp = getComponent('FOOTER');
			const buttonsComp = getComponent('BUTTONS');

			setHeaderType(headerComp?.format);
			if (
				headerComp?.format === 'IMAGE' ||
				headerComp?.format === 'DOCUMENT' ||
				headerComp?.format === 'VIDEO'
			) {
				setHeaderMedia(headerComp?.example?.header_handle?.[0]);
			}

			const headerPartsLoc = splitTextWithPlaceholders(headerComp?.text);
			const bodyPartsLoc = splitTextWithPlaceholders(bodyComp?.text);

			const buttonsArray: TemplateButton[] = (buttonsComp?.buttons || []) as TemplateButton[];
			const buttonPartsLoc: ButtonParts[] = buttonsArray.map((b) => ({
				original: b,
				text: splitTextWithPlaceholders(b.text),
				variables:
					b.type === 'COPY_CODE' || b.type === 'VOICE_CALL'
						? ['{{}}']
						: b.url
							? splitTextWithPlaceholders(b.url)
							: [],
			}));

			let totalPlaceholders =
				countPlaceholdersInText(headerComp?.text) +
				countPlaceholdersInText(bodyComp?.text) +
				countPlaceholdersInText(footerComp?.text);

			buttonsArray.forEach((b) => {
				totalPlaceholders += countPlaceholdersInText(b.text);
				totalPlaceholders += countPlaceholdersInText(b.url);
			});

			props.onChangeParams(new Array(totalPlaceholders).fill(''));

			setHeaderParts(headerPartsLoc);
			setBodyParts(bodyPartsLoc);
			setButtonParts(buttonPartsLoc);
			setFooterText(footerComp?.text ?? '');

			props.onChangeSelectedTemplate(template);
		},
		[props.templateList, props.onChangeParams, props.onChangeSelectedTemplate],
	);

	const handleParamChange = useCallback((paramIndex: number, value: string) => {
		setInternalParams((prev) => {
			const newParams = [...prev];
			newParams[paramIndex] = value;
			return newParams;
		});
	}, []);

	useEffect(() => {
		props.onChangeParams(internalParams);
	}, [internalParams]);

	const handleClickDropdown = useCallback(
		(paramIndex: number, state?: boolean) => {
			setIsDropdownOpen((prev) => {
				const newState = [...prev];
				newState[paramIndex] = state !== undefined ? state : !newState[paramIndex];
				return newState;
			});

			if (state !== undefined ? state : !isDropdownOpen[paramIndex]) {
				onSearchVariables(props.params[paramIndex] ?? '');
			}
		},
		[props.params, isDropdownOpen],
	);

	const onSearchVariables = useCallback(
		(value: string) => {
			const searchTerm = value.toLowerCase().replace(/ /g, '_');
			const foundVariables = variables.filter((variable) =>
				variable.toLowerCase().includes(searchTerm),
			);
			setFilteredVariables(foundVariables);
		},
		[variables],
	);

	const handleLocation = useCallback(
		(loc: { latitude: number; longitude: number; name?: string; address?: string }) => {
			setLocation(loc);

			const locationParameter = {
				latitude: loc.latitude,
				longitude: loc.longitude,
				name: loc.name,
				address: loc.address,
			};

			props.onChangeLocation(locationParameter);
		},
		[props.onChangeLocation],
	);

	const renderTextInput = useCallback(
		(parts: string[], startIndex: number): { nodes: React.ReactNode[]; consumed: number } => {
			let paramCursor = startIndex;
			const nodes: React.ReactNode[] = [];

			parts.forEach((part, index) => {
				if (part === '{{}}') {
					const currentParamIndex = paramCursor;
					paramCursor += 1;

					const validateStatus =
						props.paramsErrors && props.paramsErrors[currentParamIndex] ? 'error' : undefined;

					nodes.push(
						<Form.Item
							key={`input-${startIndex}-${index}`}
							validateStatus={validateStatus}
							style={{ display: 'inline-block', marginTop: '-10px', marginBottom: 0 }}
						>
							<AutoComplete
								size="small"
								style={{ minWidth: 80, display: 'inline-block' }}
								placeholder={`Parâmetro ${currentParamIndex + 1}`}
								dataSource={filteredVariables}
								onSearch={onSearchVariables}
								value={internalParams[currentParamIndex] || ''}
								onChange={(value) => handleParamChange(currentParamIndex, value as string)}
								open={isDropdownOpen[currentParamIndex]}
								onFocus={() => handleClickDropdown(currentParamIndex, true)}
								onBlur={() => setTimeout(() => handleClickDropdown(currentParamIndex, false), 100)}
								dropdownMatchSelectWidth={false}
								dropdownStyle={{
									minWidth: 150,
									right: 0,
									left: 'auto',
								}}
							>
								<Input
									style={{ width: '100%', marginBottom: 0 }}
									suffix={
										!props.isStatic && (
											<Tooltip title={dictionary.choose_variables}>
												<Button
													style={{ marginRight: -12, fontSize: 16, marginBottom: 4 }}
													type="link"
													size="small"
													onClick={() => handleClickDropdown(currentParamIndex)}
													aria-label="Abrir seletor de variáveis"
												>
													{'{ }'}
												</Button>
											</Tooltip>
										)
									}
								/>
							</AutoComplete>
						</Form.Item>,
					);
				} else {
					nodes.push(
						<span
							key={`text-${startIndex}-${index}`}
							style={{ fontSize: '14px', display: 'inline', verticalAlign: 'middle' }}
							dangerouslySetInnerHTML={{ __html: convertWhatsappTagsToHTML(part) }}
						/>,
					);
				}
			});

			return { nodes, consumed: paramCursor - startIndex };
		},
		[
			props.params,
			props.paramsErrors,
			props.isStatic,
			dictionary.choose_variables,
			filteredVariables,
			onSearchVariables,
			handleParamChange,
			handleClickDropdown,
			isDropdownOpen,
		],
	);

	const memoizedRenderTextInput = useMemo(() => renderTextInput, [renderTextInput]);

	const selectedTemplateIndex = useMemo(() => {
		if (!props.selectedTemplate) return -1;
		return props.templateList.findIndex((t) => t.id === props.selectedTemplate?.id);
	}, [props.selectedTemplate, props.templateList]);

	const headerRendered = useMemo(
		() => memoizedRenderTextInput(headerParts, 0),
		[headerParts, memoizedRenderTextInput],
	);

	const bodyRendered = useMemo(
		() => memoizedRenderTextInput(bodyParts, headerRendered.consumed),
		[bodyParts, headerRendered.consumed, memoizedRenderTextInput],
	);

	let buttonParamsCursor = headerRendered.consumed + bodyRendered.consumed;

	return (
		<>
			<Row style={{ height: 75, paddingTop: '2em' }}>
				<Title level={2}>
					<Text type="gray-9">{`${dictionary.select_campaign_template}:`}</Text>
				</Title>
			</Row>

			<Row className="pa-0" style={{ minHeight: 150 }} align="top" justify="start">
				<Col span={24}>
					<Form.Item
						style={{ marginBottom: 0 }}
						validateStatus={templateErrorStatus}
						help={props.templateError}
					>
						<Select
							{...{
								defaultValue: selectedTemplateIndex >= 0 ? selectedTemplateIndex : undefined,
								size: 'large',
								style: { width: '100%', margin: '5px 0' },
								placeholder: dictionary.template,
								onChange: (index) => handleTemplateChange(index as number),
								loading: !props.templateFetchError && props.templateList.length === 0,
								disabled: !!props.templateFetchError,
								showSearch: true,
								optionFilterProp: 'children',
								filterOption: (input, option) => {
									return (option?.props.children as string)
										.toLowerCase()
										.includes(input.toLowerCase());
								},
							}}
						>
							{props.templateList.map((element) => (
								<Select.Option
									key={element.id ?? element.name}
									value={props.templateList.indexOf(element)}
								>
									{element.name + ' - ' + (element.languageDescription ?? element.language)}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					{props.templateFetchError && (
						<Alert
							message={dictionary.template_error}
							description={
								<div>
									<p>{props.templateFetchError.message || dictionary.unknown_error}</p>
									<p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
										<strong>{dictionary.possible_cause}:</strong>{' '}
										{dictionary.template_fetch_permission_error}
									</p>
								</div>
							}
							type="error"
							showIcon
							style={{ marginTop: '10px' }}
						/>
					)}
					{currentTemplate && (
						<Card
							title="Modelo"
							headStyle={{
								backgroundColor: '#075E54',
								color: 'white',
								borderRadius: '6px 6px 0 0',
								textAlign: 'left',
							}}
							bodyStyle={{
								backgroundColor: '#ece5dd',
								borderRadius: '0 0 6px 6px',
								textAlign: 'left',
							}}
							style={{ width: '100%', marginTop: 16, overflow: 'hidden' }}
						>
							<div
								style={{
									backgroundColor: 'white',
									borderRadius: 12,
									padding: '12px 16px',
									maxWidth: '90%',
								}}
							>
								{/* Cabeçalho */}
								{(headerParts.length > 0 || headerType) && (
									<div
										style={{
											fontSize: 14,
											paddingBottom: 8,
											borderBottom: '1px solid #e0e0e0',
											width: '100%',
										}}
									>
										{headerParts.length > 0 && headerRendered.nodes}

										{headerType === 'IMAGE' && (
											<div style={{ display: 'flex', justifyContent: 'center' }}>
												<img
													src={headerMedia}
													alt="Header"
													style={{
														width: '400px',
														height: '400px',
														objectFit: 'cover',
													}}
												/>
											</div>
										)}

										{headerType === 'VIDEO' && (
											<div style={{ display: 'flex', justifyContent: 'center' }}>
												<video
													src={headerMedia}
													controls
													style={{
														width: '400px',
														height: '400px',
														objectFit: 'cover',
													}}
												/>
											</div>
										)}

										{headerType === 'DOCUMENT' && (
											<a
												href={headerMedia}
												target="_blank"
												rel="noopener noreferrer"
												style={{
													display: 'flex',
													justifyContent: 'center',
													marginTop: 8,
												}}
											>
												<div
													style={{
														color: '#0088cc',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														fontWeight: 500,
													}}
												>
													<FormattedMessage id="view_header_document" />
												</div>
											</a>
										)}

										{headerType === 'LOCATION' && (
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													width: '100%',
													gap: 8,
												}}
											>
												<LocationSearch onSelect={handleLocation} />

												{location && (
													<div
														style={{
															border: '1px solid #ddd',
															borderRadius: 8,
															padding: 10,
															backgroundColor: '#fafafa',
															width: '100%',
														}}
													>
														<div style={{ fontWeight: 600 }}>📍 {location.name}</div>

														{location.address && (
															<div style={{ fontSize: 13, opacity: 0.85 }}>{location.address}</div>
														)}

														<div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
															Lat: {location.latitude} • Lng: {location.longitude}
														</div>
													</div>
												)}
											</div>
										)}
									</div>
								)}

								{/* Renderiza Corpo */}
								<div style={{ fontSize: 14, marginBottom: 8 }}>{bodyRendered.nodes}</div>

								{/* Renderiza Rodapé de Texto */}
								<div
									style={{
										fontSize: 12,
										marginTop: 8,
										display: 'flex',
										justifyContent: 'space-between',
									}}
								>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{footerText ? convertWhatsappTagsToHTML(footerText) : null}
									</Text>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
									</Text>
								</div>

								{/* Renderiza Botões */}
								{buttonParts.length > 0 && (
									<div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
										{buttonParts.map((button, bidx) => {
											const urlRendered = button.variables.length
												? memoizedRenderTextInput(button.variables, buttonParamsCursor)
												: { nodes: [], consumed: 0 };
											const consumedUrl = urlRendered.consumed;
											buttonParamsCursor += consumedUrl;

											return (
												<div
													key={`btn-${bidx}`}
													style={{
														textDecoration: 'none',
														fontSize: 14,
														color: '#0088cc',
														borderTop: '1px solid #eee',
														display: 'flex',
														flexDirection: 'row',
														alignItems: 'center',
														justifyContent: 'center',
														paddingTop: 5,
														paddingBottom: 0,
														marginBottom: 0,
													}}
												>
													<span style={{ display: 'flex', alignItems: 'center' }}>
														<span>
															{button.text}
															{urlRendered.nodes.filter((node: any) => node.type !== 'span')}
														</span>
													</span>
												</div>
											);
										})}
									</div>
								)}
							</div>
						</Card>
					)}
				</Col>
			</Row>

			<Row style={{ height: 75 }} />
		</>
	);
}

export default CampaignTemplateMessage;
